import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { cleanTicketId, isTicketFormatValid } from '../../utils/worklogRules';
import { fetchAllowedActivityIds, updateWorklog } from '../../utils/worklogService';
import { MUTED } from '../home/homeTheme';

const customerName = (p) => (typeof p?.customer === 'string' ? p.customer : p?.customer?.name || '');

/**
 * Cambio ticket dei worklog selezionati (sostituisce il wizard "Bulk edit tickets").
 * Come nella pagina classica la data non viene inviata, così il cambio vale anche sui mesi bloccati;
 * progetto e attività si possono cambiare solo verso progetti che l'utente gestisce.
 */
export default function ChangeTicketDialog({ open, rows, projects, managedProjects, activities, onClose, onDone }) {
  const [ticket, setTicket] = useState('');
  const [project, setProject] = useState(null);
  const [activity, setActivity] = useState(null);
  const [allowedIds, setAllowedIds] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTicket('');
    setProject(null);
    setActivity(null);
    setError(null);
    setProgress(null);
  }, [open]);

  useEffect(() => {
    setActivity(null);
    setAllowedIds(null);
    if (!project) return undefined;
    let cancelled = false;
    fetchAllowedActivityIds(project.id).then((ids) => !cancelled && setAllowedIds(ids));
    return () => {
      cancelled = true;
    };
  }, [project]);

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const activityById = useMemo(() => new Map(activities.map((a) => [a.id, a])), [activities]);
  const activityOptions = allowedIds && allowedIds.length ? activities.filter((a) => allowedIds.includes(a.id)) : activities;
  const currentTickets = [...new Set(rows.map((w) => String(w.ticket_id || '').trim() || '(none)'))];
  const processing = progress && progress.current < progress.total;

  const apply = async () => {
    if (!ticket.trim()) {
      setError('Enter the new ticket ID.');
      return;
    }
    // Formato del ticket controllato sul progetto finale, quando l'attività richiede un ticket
    const invalid = new Set();
    const planned = rows.map((w) => {
      const finalProject = project || projectById.get(w.project_id);
      const finalActivity = activity || activityById.get(w.activity_id);
      const ticketId = cleanTicketId(ticket, finalProject?.ticket_id_prefix);
      if (finalActivity?.requires_ticket_id && finalProject && !isTicketFormatValid(finalProject, ticketId)) invalid.add(finalProject);
      return { w, ticketId, projectId: project ? project.id : w.project_id, activityId: activity ? activity.id : w.activity_id };
    });
    if (invalid.size) {
      const p = [...invalid][0];
      setError(`The ticket doesn't match the format of ${p.name}${p.regex_desc ? `: ${p.regex_desc}` : ''}.`);
      return;
    }
    setError(null);
    setProgress({ current: 0, total: planned.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < planned.length; i++) {
      const { w, ticketId, projectId, activityId } = planned[i];
      try {
        await updateWorklog({
          id: w.id,
          ticket_id: ticketId,
          project_id: projectId,
          activity_id: activityId,
          user_id: w.user_id,
          effort_type_id: w.effort_type_id,
          effort: w.effort,
          description: w.description || '',
        });
      } catch (e) {
        errors += 1;
      }
      setProgress({ current: i + 1, total: planned.length, errors });
    }
    onDone({ updated: planned.length - errors, errors });
  };

  return (
    <Dialog open={open} onClose={() => !processing && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Change ticket</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: MUTED, mb: 2 }}>
          {rows.length} worklog{rows.length === 1 ? '' : 's'} selected · current ticket{currentTickets.length === 1 ? '' : 's'}:{' '}
          {currentTickets.slice(0, 5).join(', ')}
          {currentTickets.length > 5 ? ` and ${currentTickets.length - 5} more` : ''}
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="New ticket ID"
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
            required
            autoFocus
            size="small"
            disabled={!!progress}
          />
          <Autocomplete
            size="small"
            options={managedProjects}
            value={project}
            onChange={(_, v) => setProject(v)}
            getOptionLabel={(p) => p.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, p) => (
              <Box component="li" {...props} key={p.id} sx={{ display: 'block !important' }}>
                <Typography variant="body2">{p.name}</Typography>
                {customerName(p) && (
                  <Typography variant="caption" color="text.secondary">
                    {customerName(p)}
                  </Typography>
                )}
              </Box>
            )}
            disabled={!!progress}
            renderInput={(params) => <TextField {...params} label="Move to project (optional)" placeholder="Keep the current project" InputLabelProps={{ shrink: true }} />}
          />
          <Autocomplete
            size="small"
            options={activityOptions}
            value={activity}
            onChange={(_, v) => setActivity(v)}
            getOptionLabel={(a) => a.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={!project || !!progress}
            renderInput={(params) => (
              <TextField {...params} label="Change activity (optional)" placeholder={project ? 'Keep the current activity' : 'Choose a project first'} InputLabelProps={{ shrink: true }} />
            )}
          />
        </Box>
        {activity?.requires_description && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This activity requires a description: worklogs without one keep an empty description.
          </Alert>
        )}
        <Alert severity="warning" sx={{ mt: 2 }}>
          The change can’t be undone and also applies to worklogs in locked months.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {progress && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} color="success" sx={{ height: 8, borderRadius: 4 }} />
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>
              {progress.current} of {progress.total} updated{progress.errors ? ` · ${progress.errors} failed` : ''}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={!!processing} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={apply}
          disabled={!!progress}
          sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
        >
          Change {rows.length} worklog{rows.length === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
